/// <reference path="./node-globals.d.ts" />
import { Injectable } from "@angular/core";

import { EncryptionCoreService } from "../encryption-core/encryption-core.service";
import { KeyAssemblyService } from "../encryption-core/key-assembly.service";

@Injectable()
export class SsrAuthService {
  private encryptionCore: EncryptionCoreService;

  constructor() {
    // Direct instantiation for SSR usage
    const keyAssembly = new KeyAssemblyService();
    this.encryptionCore = new EncryptionCoreService(keyAssembly);
  }

  async generateSignature(
    method: string,
    url: string,
    body: any
  ): Promise<{
    signature: string;
    timeBlock: number;
    timestamp: number;
  }> {
    // Get current time and time block
    const timestamp = Math.floor(Date.now() / 1000);
    const timeBlock = this.encryptionCore.getTimeBlock();

    // Derive key from time block
    const key = await this.encryptionCore.deriveKey(timeBlock);

    // Create message to sign
    const bodyString = body ? JSON.stringify(body) : "";
    const message = `${method}:${url}:${timestamp}:${bodyString}`;

    // Generate HMAC signature
    let signature: string;

    if (typeof require !== "undefined") {
      // Node.js environment
      try {
        const crypto = eval("require")("crypto");
        signature = crypto.createHmac("sha256", Buffer.from(key)).update(message).digest("hex");
      } catch (e) {
        // Fallback if require is not available
        signature = this.fallbackHash(message, key);
      }
    } else {
      // Fallback for environments without Node crypto
      signature = this.fallbackHash(message, key);
    }

    return {
      signature,
      timeBlock,
      timestamp
    };
  }

  /**
   * Verify a signature (for testing purposes)
   */
  async verifySignature(
    method: string,
    url: string,
    body: any,
    signature: string,
    timeBlock: number,
    timestamp: number
  ): Promise<boolean> {
    const expected = await this.generateSignature(method, url, body);

    // Check if timestamp is within acceptable window (5 minutes)
    const currentTime = Math.floor(Date.now() / 1000);
    if (Math.abs(currentTime - timestamp) > 300) {
      return false;
    }

    // Use the provided timeBlock for verification
    const key = await this.encryptionCore.deriveKey(timeBlock);
    const bodyString = body ? JSON.stringify(body) : "";
    const message = `${method}:${url}:${timestamp}:${bodyString}`;

    if (typeof require !== "undefined") {
      try {
        const crypto = eval("require")("crypto");
        const expectedSignature = crypto.createHmac("sha256", Buffer.from(key)).update(message).digest("hex");

        // Constant-time comparison
        try {
          return crypto.timingSafeEqual(Buffer.from(signature, "hex"), Buffer.from(expectedSignature, "hex"));
        } catch (error) {
          // If buffers are different lengths or invalid hex, return false
          return false;
        }
      } catch (e) {
        // Fallback if require is not available
        const expectedSignature = this.fallbackHash(message, key);
        return signature === expectedSignature;
      }
    }

    // Fallback comparison (not timing-safe)
    return signature === expected.signature;
  }

  private fallbackHash(message: string, key: Uint8Array): string {
    // Simple hash for testing (not secure for production)
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const keyBytes = Array.from(key);
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      hash = (hash << 5) - hash + data[i] + keyBytes[i % keyBytes.length];
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }
}
